import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Building,
  MapPin,
  Target,
  TrendingUp,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Phone,
  Mail,
  Video,
  UserCheck,
  RefreshCw,
  Bell,
  AlertCircle,
  Star,
  MessageSquare,
  PhoneCall,
  Mail as MailIcon,
  Video as VideoIcon,
  Calendar as CalendarIcon,
  CheckSquare,
  Square,
  Zap,
  ArrowRight,
  ArrowLeft,
  RotateCcw
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { Breadcrumb } from '../../components/Common'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { followUpService } from '../../services/followUpService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const FollowUp = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalFollowUps: 0,
    pendingFollowUps: 0,
    completedFollowUps: 0,
    overdueFollowUps: 0,
    todayFollowUps: 0,
    thisWeekFollowUps: 0,
    highPriorityFollowUps: 0,
    completedThisWeek: 0,
    completionRate: 0,
    averageResponseTime: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  const breadcrumbItems = [
    { label: 'Home', icon: Home, path: '/dashboard' },
    { label: 'CRM', icon: Users, path: '/crm' },
    { label: 'Follow-ups', icon: RefreshCw }
  ]

  const columns = [
    {
      key: 'followUpCode',
      label: 'Follow-up Code',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
            <RefreshCw size={16} className="text-purple-600" />
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{row.followUpType}</div>
          </div>
        </div>
      )
    },
    {
      key: 'followUpDate',
      label: 'Follow-up Date',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-gray-400" />
          <div>
            <div className="font-medium text-gray-900">
              {new Date(value).toLocaleDateString()}
            </div>
            <div className="text-sm text-gray-500">
              {new Date(value).toLocaleTimeString()}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'relatedTo',
      label: 'Related To',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <UserCheck size={16} className="text-blue-600" />
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{row.relatedModule}</div>
          </div>
        </div>
      )
    },
    {
      key: 'followUpStatus',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusConfig = {
          'Pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
          'Completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
          'In Progress': { color: 'bg-blue-100 text-blue-800', icon: Activity },
          'Overdue': { color: 'bg-red-100 text-red-800', icon: AlertCircle },
          'Cancelled': { color: 'bg-gray-100 text-gray-800', icon: Square }
        }
        const config = statusConfig[value] || statusConfig['Pending']
        const Icon = config.icon
        return (
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <Icon size={12} />
            {value}
          </span>
        )
      }
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (value) => {
        const priorityConfig = {
          'High': { color: 'bg-red-100 text-red-800', icon: AlertCircle },
          'Medium': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
          'Low': { color: 'bg-green-100 text-green-800', icon: CheckCircle }
        }
        const config = priorityConfig[value] || priorityConfig['Medium']
        const Icon = config.icon
        return (
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <Icon size={12} />
            {value}
          </span>
        )
      }
    },
    {
      key: 'followUpMethod',
      label: 'Method',
      sortable: true,
      render: (value) => {
        const methodConfig = {
          'Phone Call': { color: 'bg-blue-100 text-blue-800', icon: Phone },
          'Email': { color: 'bg-green-100 text-green-800', icon: MailIcon },
          'Video Call': { color: 'bg-purple-100 text-purple-800', icon: VideoIcon },
          'Meeting': { color: 'bg-orange-100 text-orange-800', icon: CalendarIcon },
          'Message': { color: 'bg-gray-100 text-gray-800', icon: MessageSquare }
        }
        const config = methodConfig[value] || methodConfig['Phone Call']
        const Icon = config.icon
        return (
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <Icon size={12} />
            {value}
          </span>
        )
      }
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
            <UserCheck size={12} className="text-gray-600" />
          </div>
          <span className="text-gray-900">{value}</span>
        </div>
      )
    }
  ]

  const loadData = async () => {
    try {
      setLoading(true)
      const response = await followUpService.getAllFollowUps()
      if (response.success) {
        setData(response.data.docs || response.data || [])
        if ((response.data.docs || response.data || []).length === 0 && !hasShownEmptyToastRef.current) {
          toast.info('No follow-up records found. Create your first follow-up record to get started.')
          hasShownEmptyToastRef.current = true
        }
      } else {
        toast.error(response.message || 'Failed to load follow-up records')
        setData([])
      }
    } catch (error) {
      console.error('Error loading follow-up records:', error)
      toast.error('Failed to load follow-up records')
      setData([])
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const response = await followUpService.getFollowUpStats()
      if (response.success) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('Error loading follow-up stats:', error)
    }
  }

  useEffect(() => {
    if (!hasLoadedRef.current) {
      loadData()
      loadStats()
      hasLoadedRef.current = true
    }
  }, [])

  const handleAdd = () => {
    navigate('/crm/follow-up/new')
  }

  const handleEdit = (row) => {
    navigate(`/crm/follow-up/edit/${row._id}`)
  }

  const handleView = (row) => {
    navigate(`/crm/follow-up/view/${row._id}`)
  }

  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await followUpService.deleteFollowUp(deleteItem._id)
      if (response.success) {
        toast.success('Follow-up record deleted successfully')
        setData(data.filter(item => item._id !== deleteItem._id))
        loadStats() // Refresh stats after deletion
      } else {
        toast.error(response.message || 'Failed to delete follow-up record')
      }
    } catch (error) {
      console.error('Error deleting follow-up record:', error)
      toast.error('Failed to delete follow-up record')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.warning('Please select follow-up records to delete')
      return
    }

    try {
      const response = await followUpService.bulkDeleteFollowUps(selectedRows)
      if (response.success) {
        toast.success(`${selectedRows.length} follow-up records deleted successfully`)
        setData(data.filter(item => !selectedRows.includes(item._id)))
        setSelectedRows([])
        loadStats() // Refresh stats after bulk deletion
      } else {
        toast.error(response.message || 'Failed to delete follow-up records')
      }
    } catch (error) {
      console.error('Error bulk deleting follow-up records:', error)
      toast.error('Failed to delete follow-up records')
    }
  }


  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Follow-up Management</h1>
          <p>Track and manage all your follow-up activities, reminders, and outcomes</p>
        </div>
        <div className="page-actions">
          <button
            onClick={handleAdd}
            className="btn-primary"
          >
            <Plus size={16} />
            Add Follow-up
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <RefreshCw size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalFollowUps || 0}</div>
          <div className="metric-title">Total Follow-ups</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.pendingFollowUps || 0}</div>
          <div className="metric-title">Pending</div>
          <div className="metric-subtitle">Awaiting action</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.completedFollowUps || 0}</div>
          <div className="metric-title">Completed</div>
          <div className="metric-subtitle">Successfully closed</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertCircle size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">{stats.overdueFollowUps || 0}</div>
          <div className="metric-title">Overdue</div>
          <div className="metric-subtitle">Requires attention</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search follow-ups..."
          emptyMessage="No follow-up records found. Create your first follow-up to get started."
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Follow-up Record"
        message={`Are you sure you want to delete the follow-up record "${deleteItem?.followUpCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default FollowUp
